import { useForm } from "react-hook-form";
import CustomInput from "../CustomInput";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Form } from "../ui/form";
import {
  createCustomerModalSchema,
  CreateCustomerModalSchemaType,
} from "./createCustomerModalSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { CustomerAndPartnerSelectionOption } from "./hooks/useCustomerAndPartnerSelectionHandler";
import {
  accessControl,
  countryCityDefaultValue,
  defaultCountryCode,
  defaultPagination,
  inputPlaceHolder,
} from "src/constant";
import { useEffect } from "react";
import { getCountries, getCountryCities } from "src/utils/getCountries";
import PermissionTooltip from "../PermissionTooltip";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";
import CheckBoxInput from "../CheckBoxInput";
import { useGetSalespersonList } from "src/pages/UserManagement/SalespersonList/api/queries";
import useSearch from "src/hooks/useSearch";
import { formatPhoneNumber } from "src/utils/formatPhoneNumber";

interface CreateCustomerModalProps {
  searchInput: string;
  handleChangeSearchInput: (search: string) => void;
  isShow: boolean;
  onClose: () => void;
  onValueChange: (value: CustomerAndPartnerSelectionOption) => void;
}

const customerDefaultValue = {
  name: "",
  phoneNumber: defaultCountryCode,
  country:
    process.env.REACT_APP_PRE_IS_PRE_FILL_COUNTRY_AND_CITY === "1"
      ? countryCityDefaultValue.country
      : undefined,
  city:
    process.env.REACT_APP_PRE_IS_PRE_FILL_COUNTRY_AND_CITY === "1"
      ? countryCityDefaultValue.city
      : undefined,
  address: undefined,
  isAssignSalesperson: false,
  salespersonId: undefined,
  contractDatePeriod: null,
};

const CreateCustomerModal = ({
  searchInput,
  handleChangeSearchInput,
  isShow,
  onClose,
  onValueChange,
}: CreateCustomerModalProps) => {
  const isAllowedCustomerListAssignSalesperson = useGetAccessPermission({
    resource:
      AccessResource[
        "User Management - Customer List | Allow Assign Salesperson"
      ],
    action: ActionType["Allow"],
  });

  const {
    value: searchSalespersonValue,
    search: searchSalesperson,
    setSearch: setSearchSalesperson,
  } = useSearch();

  const { data: salespersons, isLoading: isLoadingSalesperson } =
    useGetSalespersonList({
      ...defaultPagination,
      country: [],
      city: [],
      salespersonListNameOrPhoneNumber: searchSalespersonValue,
      is_active: 1,
    });

  const countries = getCountries();

  const form = useForm<CreateCustomerModalSchemaType>({
    resolver: zodResolver(createCustomerModalSchema),
    defaultValues: {
      ...customerDefaultValue,
      name: searchInput ?? "",
    },
  });

  const formValues = form.getValues();

  const selectedCountry = countries.find(
    (country) => country.value === formValues.country
  );
  const countryCities = getCountryCities(selectedCountry?.countryCode);

  const handleAddCustomer = form.handleSubmit(() => {
    onValueChange({
      label: "",
      value: "",
      name: formValues.name,
      phoneNumber: formValues.phoneNumber,
      birthdate: formValues.birthdate,
      country: formValues.country,
      city: formValues.city,
      address: formValues.address,
      type: "customer",
      customerTypeId: "",
      customerType: "",
      typeHex: "",
      isAssignSalesperson: formValues.isAssignSalesperson,
      salespersonId: formValues.salespersonId,
      contractDatePeriod: formValues.contractDatePeriod,
    });

    handleCloseModal();
  });

  const handleCloseModal = () => {
    onClose();
    form.reset(customerDefaultValue);
  };

  useEffect(() => {
    if (isShow) {
      form.reset({
        ...customerDefaultValue,
        name: searchInput,
      });
      handleChangeSearchInput("");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, form]);

  const salespersonOptions =
    salespersons?.data?.map((salesperson) => ({
      label: `${salesperson.name}${
        salesperson.phone_number
          ? ` - ${formatPhoneNumber(salesperson.phone_number)}`
          : ""
      }`,
      value: `${salesperson.id}`,
    })) || [];

  const handleSearchSalesperson = (search: string) =>
    setSearchSalesperson(search);

  return (
    <Dialog open={isShow} onOpenChange={handleCloseModal}>
      <DialogContent className="min-w-[1000px]">
        <DialogHeader>
          <DialogTitle className="text-md font-semibold text-darkBrown-default">
            Create Customer
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div className="flex flex-col items-center gap-4 pt-2 pb-5 ">
            <CustomInput
              form={form}
              name="name"
              labelText="Customer Name"
              placeholder="Input name"
            />

            <div className="w-full grid grid-cols-2 gap-4">
              <CustomInput
                form={form}
                name="phoneNumber"
                labelText="Customer Phone Number"
                placeholder={inputPlaceHolder["phone_number"]}
                variant="phone-number"
              />

              <CustomInput
                form={form}
                name="birthdate"
                labelText="Birthdate (Optional)"
                placeholder="Select Date"
                variant="date"
                dateType="SINGLE"
                maxDate={new Date()}
              />
            </div>

            <>
              <div className="w-full grid grid-cols-2 gap-4">
                <CustomInput
                  form={form}
                  name="country"
                  labelText="Country"
                  placeholder="Input Country"
                  variant="select"
                  options={countries}
                  onChange={() => {
                    form.setValue("city", "", {
                      shouldValidate: true,
                    });
                    form.setValue("address", "", {
                      shouldValidate: true,
                    });
                  }}
                />

                {countryCities.length > 0 ? (
                  <CustomInput
                    disabled={!formValues.country}
                    form={form}
                    name="city"
                    labelText="City"
                    placeholder="Input City"
                    variant="select"
                    options={countryCities}
                  />
                ) : (
                  <CustomInput
                    disabled={!formValues.country}
                    form={form}
                    name="city"
                    labelText="City"
                    placeholder="Input City"
                  />
                )}
              </div>

              <CustomInput
                form={form}
                name="address"
                labelText="Address (Optional)"
                placeholder="Input Full Address"
              />
            </>

            <PermissionTooltip
              permissions={[
                {
                  label:
                    accessControl.userManagementCustomerListAllowAssignSalesperson,
                  allowed: isAllowedCustomerListAssignSalesperson,
                },
              ]}
            >
              <div className="w-full">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-sm font-normal">
                    Assign Salesperson
                  </span>

                  <CheckBoxInput
                    disabled={!isAllowedCustomerListAssignSalesperson}
                    form={form}
                    name="isAssignSalesperson"
                    labelText="Assign Salesperson"
                    size="default"
                  />
                </div>

                {formValues.isAssignSalesperson && (
                  <div className="flex flex-col gap-4">
                    <h6 className="font-semibold -mb-2 mt-2">
                      Salesperson Detail
                    </h6>

                    <CustomInput
                      form={form}
                      name="salespersonId"
                      labelText="Salesperson"
                      placeholder="Select Salesperson by Name or Phone Number"
                      variant="select"
                      options={salespersonOptions}
                      isLoading={isLoadingSalesperson}
                      searchValue={searchSalesperson}
                      onSearch={handleSearchSalesperson}
                    />

                    <div className="flex flex-1 flex-col gap-1">
                      <CustomInput
                        form={form}
                        name="contractDatePeriod"
                        labelText="Contract Date Period (optional)"
                        placeholder="Select Date Range"
                        variant="date"
                        dateType="RANGE"
                      />

                      {!formValues.contractDatePeriod?.from && (
                        <span className="text-muted-foreground text-xs">{`Without a contract date, the customer will be assigned starting from today with no end date.`}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </PermissionTooltip>
          </div>
        </Form>

        <DialogFooter>
          <Button
            type="submit"
            onClick={handleAddCustomer}
            isLoading={form.formState.isSubmitting}
          >
            Add Customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomerModal;
